import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Clock, Filter, AlertTriangle } from 'lucide-react';
import { approvalsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const ApprovalQueuePage = () => {
  const { user } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved_today: 0, rejected_today: 0 });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        approvalsApi.list({ status: filter }),
        approvalsApi.stats(),
      ]);
      setApprovals(listRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await approvalsApi.approve(id);
      toast.success('Action approved and executed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
    setActionLoading(null);
  };

  const handleReject = async (id) => {
    setActionLoading(id);
    try {
      await approvalsApi.reject(id, { comments: 'Rejected by checker' });
      toast.success('Action rejected');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    }
    setActionLoading(null);
  };

  const fmt = (v) => v != null ? `R ${Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : '-';

  return (
    <div data-testid="approval-queue-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-[#1f1f1f]">Approval Queue</h1>
          <p className="text-sm text-gray-500">Maker-Checker: Review and approve pending actions</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-amber-50 border border-amber-200 p-4" data-testid="stats-pending">
          <div className="flex items-center gap-2 text-amber-700">
            <Clock className="w-5 h-5" />
            <span className="text-2xl font-bold">{stats.pending}</span>
          </div>
          <p className="text-xs text-amber-600 mt-1">Pending Approval</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 p-4" data-testid="stats-approved">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle className="w-5 h-5" />
            <span className="text-2xl font-bold">{stats.approved_today}</span>
          </div>
          <p className="text-xs text-emerald-600 mt-1">Approved Today</p>
        </div>
        <div className="bg-red-50 border border-red-200 p-4" data-testid="stats-rejected">
          <div className="flex items-center gap-2 text-red-700">
            <XCircle className="w-5 h-5" />
            <span className="text-2xl font-bold">{stats.rejected_today}</span>
          </div>
          <p className="text-xs text-red-600 mt-1">Rejected Today</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {['pending', 'approved', 'rejected'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
              filter === f ? 'bg-[#1f1f1f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Queue */}
      {loading ? (
        <div className="py-8 text-center text-gray-400 text-sm">Loading...</div>
      ) : approvals.length === 0 ? (
        <div className="py-12 text-center">
          <CheckCircle className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No {filter} items</p>
        </div>
      ) : (
        <div className="space-y-3">
          {approvals.map(item => (
            <div key={item.id} className="bg-white border border-gray-200 p-4" data-testid={`approval-${item.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${
                      item.action === 'disburse_loan' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {(item.action || '').replace(/_/g, ' ')}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${
                      item.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      item.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {item.status}
                    </span>
                  </div>

                  <p className="text-sm font-medium text-[#1f1f1f]">
                    {item.entity_type === 'loan' && `Loan Disbursement`}
                    {item.payload?.loan_number && ` - ${item.payload.loan_number}`}
                    {item.payload?.principal && ` - ${fmt(item.payload.principal)}`}
                  </p>

                  <div className="flex gap-4 mt-1 text-xs text-gray-400">
                    <span>Submitted by: <strong className="text-gray-600">{item.maker_name}</strong></span>
                    <span>{new Date(item.created_at).toLocaleString()}</span>
                    {item.checker_name && <span>Reviewed by: <strong className="text-gray-600">{item.checker_name}</strong></span>}
                  </div>

                  {item.comments && <p className="text-xs text-gray-500 mt-1">{item.comments}</p>}
                  {item.checker_comments && <p className="text-xs text-gray-500 mt-1 italic">Checker: {item.checker_comments}</p>}

                  {item.status === 'pending' && item.maker_id === user?.id && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-amber-600">
                      <AlertTriangle className="w-3 h-3" /> You submitted this — a different user must approve
                    </div>
                  )}
                </div>

                {item.status === 'pending' && item.maker_id !== user?.id && (
                  <div className="flex gap-2 ml-4 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(item.id)}
                      disabled={actionLoading === item.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                      data-testid={`approve-${item.id}`}
                    >
                      <CheckCircle className="w-3.5 h-3.5" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(item.id)}
                      disabled={actionLoading === item.id}
                      className="border-red-300 text-red-600 hover:bg-red-50 gap-1"
                      data-testid={`reject-${item.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApprovalQueuePage;
